import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { STRINGS } from '@/strings/strings';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api-client';
import { fireGa4SignUp } from '@/lib/analytics/gtag';
import { buildMetaAttributionPayload } from '@/lib/analytics/meta-payload';
import { isStrongPassword } from '@/lib/password-rules';
import { useAuthStore } from '@/stores/authStore';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { usePhotoUpload } from '../../use-photo-upload';
import { useCreateBookOrder } from '../../use-create-book-order';
import type { CastTextEntry } from '../../state/book-configurator-store';
import { buildCreateOrderPayload, buildUploadPlan } from './lightgate-payload';
import type { CastPhotoFiles } from './lightgate-payload';
import { TurnstileWidget } from './TurnstileWidget';
import { captureReadableFailure, isFileReadable } from '../../photo-materialize';

export type { CastPhotoFiles } from './lightgate-payload';

/**
 * v3.1 §6 line 270 · Light-Gate overlay.
 *
 * End-of-Schritt-1 registration + bulk photo upload + create-order.
 * Rendered by `PersonenRoute` when the customer clicks Weiter zum
 * Titelbild after answering block 5.
 *
 * ═══ THREE CONSTRAINTS (Sebastian ruling 2026-08-06 · Commit 3) ═══
 *
 * 1. THE OVERLAY NEVER NAVIGATES. Up to sixteen File objects sit in
 *    the parent's transient React state; a route change on mobile
 *    drops them. That is the entire reason J-01 puts the gate here
 *    and not at the top. The overlay stays mounted from open through
 *    every phase; only the final success calls `onSuccess(orderId)`
 *    for the parent to navigate.
 *
 * 2. THE UPLOAD HAPPENS ONCE, INSIDE THE OVERLAY, WITH VISIBLE
 *    PROGRESS. `usePhotoUpload.progress` drives the "N von M" line.
 *    Sixteen photos take a moment — the customer must see the
 *    progress or he'll assume the app hung.
 *
 * 3. ON ANY FAILURE THE OVERLAY STAYS AND THE PHOTOS STAY IN MEMORY.
 *    A failed create-order call MUST NOT leave the customer on a
 *    screen with his files gone. Each phase's failure is caught,
 *    surfaced as an error line + retry button — the parent's photo
 *    state is untouched.
 *
 * ═══ SEQUENCE ═══════════════════════════════════════════════════════
 *
 *   1. register-form   — email + password + terms; Weiter click →
 *                        `useAuthStore.register(...)` → success sets
 *                        the JWT in the auth store → phase moves to
 *                        upload; failure sets an error + retry.
 *   2. uploading       — for each cast entry, batch upload face +
 *                        optional full-body via `usePhotoUpload`;
 *                        progress line "N von M hochgeladen"; on
 *                        completion, phase moves to creating-order.
 *                        Failure keeps overlay + retry.
 *   3. creating-order  — POST `/api/book/orders` with cast +
 *                        photoKeys + freitext + sprache. On 201,
 *                        `onSuccess(orderId)` fires; failure keeps
 *                        overlay + retry.
 *
 * Orphan discipline: a mid-batch upload failure leaves the earlier
 * PUT keys in `book-uploads/*` without a BookOrder row. Retrying
 * `upload()` mints fresh signed URLs for every File; the old keys
 * are orphans that `[book-photos-orphan-sweep]` cleans server-side.
 *
 * ═══ STRINGS ═══════════════════════════════════════════════════════
 *
 * Every visible string is a PLACEHOLDER at Commit 3 (Sebastian
 * authorship pending). The commit-3 report lists them exhaustively
 * so replacements land in one string-edit pass. Only the
 * Beruhigungszeile is verbatim (Textdatei v2 line 244).
 */

type Phase =
  | { readonly kind: 'register' }
  | { readonly kind: 'register-error'; readonly message: string }
  // Sebastian ruling 2026-09-09 — Task 1 (login-in-overlay). Third
  // form phase alongside register. A returning customer whose session
  // expired switches from register → login via the "Du hast schon ein
  // Konto?" link; on success handleLogin drops into runUploadAndCreate
  // exactly as the returning-customer auto-fire branch does today.
  // Login failure surfaces as `login-error` with a retry button and
  // the login form intact; a link back to register lets him bail out.
  | { readonly kind: 'login' }
  | { readonly kind: 'login-error'; readonly message: string }
  | { readonly kind: 'uploading' }
  | { readonly kind: 'upload-error'; readonly message: string }
  | {
      /**
       * Sebastian ruling 2026-10-28 — per-person readability error.
       * Entered BEFORE any signed-URL mint when a photo's bytes
       * cannot be read (lazy content-URI stale, Android Chrome
       * Google-Photos picker edge). Names the first affected person;
       * CTA closes the overlay and routes the customer back to that
       * person's photo slot via `onPhotoNeedsReselect`.
       */
      readonly kind: 'photo-unreadable';
      readonly castKey: string;
      readonly personName: string;
    }
  | { readonly kind: 'creating-order' }
  | {
      readonly kind: 'create-order-error';
      readonly message: string;
      /**
       * Sebastian ruling 2026-09-06 evening — book-product launch gate.
       * When the backend refuses the create-order call with
       * `error_code: 'book_prelaunch_locked'` (env
       * BOOK_ORDER_ALLOWLIST_EMAILS is set and the customer's email is
       * not on it), the PWA displays the backend's verbatim
       * `user_message` and hides the retry button — a retry would just
       * fail again until the env var is cleared on 15 September. Every
       * other create-order failure keeps the placeholder + retry
       * exactly as before.
       */
      readonly suppressRetry?: boolean;
    };

// Sebastian ruling 2026-11-02 (mobile Light-Gate layout). Pre-fix:
// `top-8 translate-y-0` positioned the sheet 2rem from the top with
// no height cap, no overflow, no safe-area padding. On an Android
// 360×700 (keyboard closed) or 360×400 (keyboard open) viewport the
// sheet extended well past the bottom of the viewport and the user
// could not scroll inside it, so the "Weiter zur Geschichte" button,
// the "Du hast schon ein Konto?" link, and the Beruhigungszeile were
// unreachable (customer report 2026-11-02; PostHog: Android Chrome
// Light-Gate completion 42% vs. iOS Safari 80%).
//
// Fix mirrors the K2ConfirmDialog pattern (`K2ConfirmDialog.tsx:104`,
// Sebastian ruling 2026-09-24 for the sibling symptom):
//   - `max-h-[var(--lg-vh,calc(100dvh-2rem))]` caps the sheet to the
//     dynamic viewport (dvh shrinks on Android Chrome when the
//     keyboard opens). The `--lg-vh` CSS variable is set from
//     `window.visualViewport.height` while the overlay is open so
//     iOS Safari — where dvh does NOT shrink under the keyboard —
//     also honors the live viewport.
//   - `overflow-y-auto overscroll-contain` makes the sheet contents
//     scrollable inside the sheet; the focused input is auto-scrolled
//     into view by the browser on keyboard open.
//   - `pb-[max(1.5rem,env(safe-area-inset-bottom))]` keeps the last
//     element clear of the iPhone home indicator.
const CONTENT_CLASS =
  'max-w-[520px] top-4 translate-y-0 max-h-[var(--lg-vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain border-0 bg-dream-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-xl';
const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const FIELD_CLASS = 'builder-field h-11 w-full px-3 text-base text-dream-navy outline-none';
const ERROR_CLASS = 'text-sm text-error';

export interface LightGateOverlayProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly cast: readonly CastTextEntry[];
  readonly photos: ReadonlyMap<string, CastPhotoFiles>;
  readonly freitext: string;
  readonly sprache: string;
  /**
   * Block 2 commit 1 (Sebastian ruling 2026-08-31) — optional
   * book-level title wish. Passed through to the DTO's
   * `titelwunsch` field. Empty string when the customer skipped
   * the step; the anchor-title service writes its own title in
   * that case (fallback preserved).
   */
  readonly titelwunsch: string;
  /**
   * PROTOTYPE – feat/story-step-stations-prototype (Sebastian GO
   * 2026-10-08 round 2). The step-1 path pick from the
   * Geschichtenschritt. `null` → omit from payload (back-compat).
   */
  readonly storyKind: 'erlebt' | 'erfunden' | null;
  /** Called with the freshly-created book order id — parent navigates. */
  readonly onSuccess: (orderId: string) => void;
  /**
   * Jugendschutz Titelwunsch blocklist (Sebastian ruling 2026-09-10
   * evening) — invoked when the backend refuses create-order with
   * `error_code: 'titelwunsch_blocked'`. Overlay closes; parent
   * reopens the Titelwunsch block on Personen and shows the verbatim
   * `user_message` under the input. Optional so tests + non-Personen
   * callers can omit it (the overlay falls back to the generic
   * create-order-error phase when the callback is not supplied).
   */
  readonly onTitelwunschRejected?: (message: string) => void;
  /**
   * Sebastian ruling 2026-10-28 — per-person photo readability error
   * routing. Invoked from the `photo-unreadable` phase's CTA.
   * Parent clears the photo for `castKey` and reopens Block 1
   * (Personen) so the customer can re-pick that specific photo.
   * Optional for test ergonomics and non-Personen callers.
   */
  readonly onPhotoNeedsReselect?: (castKey: string) => void;
}

export function LightGateOverlay({
  open,
  onOpenChange,
  cast,
  photos,
  freitext,
  sprache,
  titelwunsch,
  storyKind,
  onSuccess,
  onTitelwunschRejected,
  onPhotoNeedsReselect,
}: LightGateOverlayProps) {
  const register = useAuthStore((s) => s.register);
  const login = useAuthStore((s) => s.login);
  const user = useAuthStore((s) => s.user);
  const photoUpload = usePhotoUpload();
  const createOrder = useCreateBookOrder();

  // §A4 "Bestandskunden (Audio-Abonnenten): überspringen das Gate."
  // A logged-in customer arriving at the Light-Gate has already agreed
  // to the terms, already verified (or not — verification is a
  // Bestellung-time gate) at account creation long ago. The overlay
  // therefore skips register + terms + beruhigungszeile entirely for
  // him and jumps straight into upload+create-order.
  //
  // `isReturningCustomer` is captured at MOUNT time via the initial
  // phase selection below and via `autoFireRef`. The overlay lifecycle
  // is short (one attempt from open through success/error), so mount
  // capture is honest — user cannot change from set → null inside the
  // overlay, and null → set only happens via the register call in
  // handleRegister which sets phase manually.
  const isReturningCustomer = user !== null;

  const [phase, setPhase] = useState<Phase>(() =>
    isReturningCustomer ? { kind: 'uploading' } : { kind: 'register' },
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked opt-out box.
  // `marketingAccepted` tracks whether the user wants to receive the
  // follow-up mails; sent to the backend as `marketingObjection =
  // !marketingAccepted`. Does not gate submit.
  const [marketingAccepted, setMarketingAccepted] = useState(true);
  // §A10 Turnstile token — set by the widget's success callback,
  // cleared on expiry/error. Only the anonymous branch renders the
  // widget; the returning-customer branch never calls register so
  // needs no token.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Sebastian ruling 2026-10-28 — in-flight guard. Replaces the
  // boolean `startedRef` latch. All callers (handleRegister,
  // handleLogin, auto-fire effect, retry button) converge on the
  // single in-flight promise instead of starting a second run. On
  // completion (success OR failure) the ref clears so a legitimate
  // retry can start a fresh run. The sibling `pending` state drives
  // the disabled attributes on the retry / register / login buttons
  // for the UI; refs do not re-render.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const [pending, setPending] = useState(false);
  // Double-submit guard (2026-10-30). The sibling `pending` flag
  // only flips to true INSIDE `runUploadAndCreate`, which runs
  // AFTER `register()` / `login()` resolve — so a rapid double tap
  // or a form submit + Enter within the same tick could fire the
  // handler twice before `pending` catches up. The ref gates the
  // handler synchronously (survives event-handler reentry inside
  // one tick); the mirrored state drives the button disable so the
  // UI reflects the gate on the next render.
  const authSubmittingRef = useRef(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // Build the uploadable payload — pure, no side effects. Enters
  // the `photo-unreadable` phase on the first readability failure
  // and returns null so the caller aborts BEFORE minting any
  // signed URL. Captures each failure to Sentry with safe tags.
  const checkPhotosReadable = useCallback(async (): Promise<
    | { readonly ok: true }
    | { readonly ok: false; readonly castKey: string; readonly personName: string }
  > => {
    const total = cast.length;
    for (let i = 0; i < cast.length; i += 1) {
      const entry = cast[i]!;
      const slot = photos.get(entry.key);
      const file = slot?.face ?? null;
      if (file === null) {
        return { ok: false, castKey: entry.key, personName: entry.name };
      }
      const readable = await isFileReadable(file);
      if (!readable) {
        captureReadableFailure('preupload', file, { index: i, total });
        return { ok: false, castKey: entry.key, personName: entry.name };
      }
    }
    return { ok: true };
  }, [cast, photos]);

  const runUploadAndCreateImpl = async () => {
    // R2 (Sebastian ruling 2026-08-31 evening) — this function is
    // invoked as `void runUploadAndCreate()` in five places with no
    // outer catch. An unexpected throw (e.g. a persisted-shape drift
    // in the payload builder, per Q2 findings) would previously leave
    // the phase stuck on 'creating-order' forever. The outer catch
    // below is the safety net: ANY throw not already handled by the
    // per-step try/catches inside surfaces as `create-order-error`
    // with the retry button visible. The book-order phase is used
    // (not `upload-error`) because a throw here means the customer
    // never sees an "orderId" — from their perspective the book was
    // not created.
    try {
      setPhase({ kind: 'uploading' });

      // Sebastian ruling 2026-10-28 — pre-upload readability gate.
      // Verify every face photo is readable BEFORE minting any
      // signed URL. The user_f49e3500 incident 2026-10-28 proved
      // that one lazy content-URI reference going stale during the
      // configurator session is enough to make all N PUTs reject
      // on every retry (Promise.all discipline). Enter the
      // `photo-unreadable` phase instead; the CTA routes the
      // customer back to that person's photo slot.
      const readableCheck = await checkPhotosReadable();
      if (!readableCheck.ok) {
        setPhase({
          kind: 'photo-unreadable',
          castKey: readableCheck.castKey,
          personName: readableCheck.personName,
        });
        return;
      }

      // Ordered: cast[0].face, cast[1].face, cast[2].face, …
      const plan = buildUploadPlan(cast, photos);
      let photoKeys: readonly string[];
      try {
        photoKeys = await photoUpload.upload(plan.files);
      } catch (err) {
        setPhase({
          kind: 'upload-error',
          message: STRINGS.book.personen.photoUploadOverlayError,
        });
        // Log for triage; the user-facing message stays generic.
        console.warn('[lightgate] photo upload failed', err);
        return;
      }

      setPhase({ kind: 'creating-order' });
      try {
        // R2a (Sebastian ruling 2026-08-31 evening) — payload build
        // moves INSIDE the try. A pre-Block-2 rehydrated cast entry
        // with `undefined` on new fields threw synchronously from
        // `entry.personenNotiz.trim()` at
        // `lightgate-payload.ts` before the fetch call fired,
        // escaping the pre-fix try/catch that only wrapped
        // `createOrder.submit`. Inside the try, any payload-build
        // throw surfaces as `create-order-error` with the retry
        // button, same UI as a backend rejection. Persisted-shape
        // defence lives upstream at the store's `migrate` (R1); the
        // builder still reads the fields directly (no defensive
        // coalescing, per ruling).
        const payload = buildCreateOrderPayload({
          cast,
          plan,
          photoKeys,
          freitext,
          sprache,
          titelwunsch,
          storyKind,
        });
        // Meta CAPI attribution snapshot at book-order creation
        // (2026-10-30). Captured fresh here (not threaded through
        // `buildCreateOrderPayload`) so the moment of capture is
        // the moment the backend persists it on the row — the
        // ViewContent listener reads these columns at preview_ready.
        const metaPayload = buildMetaAttributionPayload();
        const { orderId } = await createOrder.submit({ ...payload, meta: metaPayload });
        onSuccess(orderId);
      } catch (err) {
        // Sebastian ruling 2026-09-06 evening — book-product launch
        // gate. When the backend refuses with `book_prelaunch_locked`
        // (env BOOK_ORDER_ALLOWLIST_EMAILS is set and the caller's
        // email is not on it), display the backend's verbatim
        // user_message and hide the retry button. Every other
        // create-order failure keeps the placeholder + retry exactly
        // as before.
        if (
          err instanceof ApiError &&
          err.errorCode === 'titelwunsch_blocked' &&
          typeof err.envelope?.user_message === 'string' &&
          err.envelope.user_message.length > 0 &&
          onTitelwunschRejected !== undefined
        ) {
          // Jugendschutz Titelwunsch blocklist (Sebastian ruling
          // 2026-09-10 evening). Close the overlay and hand the
          // verbatim user_message back to Personen, which reopens
          // Block 3 (Titelwunsch) with the error line under the
          // input. No retry inside the overlay — retry from the
          // block, after the customer edits the title.
          //
          // Reset startedRef AND phase BEFORE closing (Sebastian
          // ruling 2026-09-10 late evening). This is the ONLY error
          // path that CLOSES the overlay after startedRef.current
          // was set to true by the auto-fire effect at line 312-327;
          // every sister error branch (register-error, login-error,
          // upload-error, create-order-error incl. book_prelaunch_
          // locked) stays INSIDE the overlay with its own retry
          // button, so persisting the ref there is correct — the
          // customer's next click owns the same overlay lifetime.
          // Only the titelwunsch path bounces the customer OUT to
          // Personen and expects a fresh lifetime on re-entry: (a)
          // startedRef unclaimed so the auto-fire effect re-fires,
          // (b) phase back to 'uploading' so a re-open before the
          // effect re-runs does not render the stale "Wir legen
          // dein Buch an" copy from the failed attempt.
          console.warn('[lightgate] create-order refused by titelwunsch blocklist', err);
          setPhase({ kind: 'uploading' });
          onTitelwunschRejected(err.envelope.user_message);
          onOpenChange(false);
        } else if (
          err instanceof ApiError &&
          err.errorCode === 'book_prelaunch_locked' &&
          typeof err.envelope?.user_message === 'string' &&
          err.envelope.user_message.length > 0
        ) {
          setPhase({
            kind: 'create-order-error',
            message: err.envelope.user_message,
            suppressRetry: true,
          });
          console.warn('[lightgate] create-order refused by launch gate', err);
        } else {
          setPhase({
            kind: 'create-order-error',
            message: STRINGS.book.personen.lightgateCreateOrderErrorPlaceholder,
          });
          console.warn('[lightgate] create-order failed', err);
        }
      }
    } catch (err) {
      // R2b (Sebastian ruling 2026-08-31 evening) — top-level safety
      // net. Reached only if a throw escapes the inner try/catches
      // (e.g. `buildUploadPlan` throws when a cast entry has no face
      // photo, `setPhase` misbehaves, etc.). Route to
      // `create-order-error` so the customer sees the error line +
      // retry button, never the indefinite "Wir legen dein Buch an"
      // spinner. Same phase as the create-order-error branch so the
      // retry button owner logic downstream does not fork.
      setPhase({
        kind: 'create-order-error',
        message: STRINGS.book.personen.lightgateCreateOrderErrorPlaceholder,
      });
      console.warn('[lightgate] runUploadAndCreate unexpected throw', err);
    }
  };

  // Sebastian ruling 2026-10-28 — single-start wrapper. All callers
  // (auto-fire effect, handleRegister, handleLogin, retry buttons)
  // call this. If a run is already in flight they receive the SAME
  // promise instead of a fresh invocation; the ref clears on
  // completion so a legitimate retry starts a new run. The sibling
  // `pending` state is kept in sync so the UI can disable buttons
  // (refs don't re-render).
  const runUploadAndCreate = async (): Promise<void> => {
    if (inFlightRef.current !== null) {
      return inFlightRef.current;
    }
    setPending(true);
    const run = (async () => {
      try {
        await runUploadAndCreateImpl();
      } finally {
        inFlightRef.current = null;
        setPending(false);
      }
    })();
    inFlightRef.current = run;
    return run;
  };

  // Sebastian ruling 2026-11-02 (mobile Light-Gate layout). While the
  // overlay is open: (1) mirror window.visualViewport.height into the
  // `--lg-vh` CSS variable so the sheet's max-height tracks the live
  // visible area under an open mobile keyboard (iOS Safari dvh does
  // NOT shrink with the keyboard; visualViewport does); (2) tag the
  // body with `data-light-gate-open` so Cookiebot's floating renew
  // widget (`#CookiebotWidget`) can be hidden via CSS while the
  // overlay is open — otherwise it covers the lower-left corner of
  // the primary CTA.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    document.body.setAttribute('data-light-gate-open', 'true');
    const vv = window.visualViewport;
    if (vv === null || vv === undefined) {
      return () => {
        document.body.removeAttribute('data-light-gate-open');
      };
    }
    // Reserve 2rem (32px) for top-4 offset + bottom breathing room.
    const sync = () => {
      document.documentElement.style.setProperty('--lg-vh', `${Math.round(vv.height - 32)}px`);
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      document.documentElement.style.removeProperty('--lg-vh');
      document.body.removeAttribute('data-light-gate-open');
    };
  }, [open]);

  // Auto-start the upload phase for a returning customer when the
  // overlay opens. The in-flight guard above makes concurrent calls
  // idempotent, so this fires without an extra latch. Anonymous
  // customers do not enter this branch — user === null at effect
  // time, so it early-returns and waits for the manual register-form
  // submit in handleRegister.
  useEffect(() => {
    if (!open) return;
    if (user === null) return;
    void runUploadAndCreate();
    // runUploadAndCreate is defined inline and closes over cast /
    // photos / freitext / sprache. Those are input props that only
    // change when the parent re-renders with fresh data, which never
    // happens WHILE the overlay is open (PersonenRoute holds them
    // stable through the overlay lifecycle). The in-flight guard
    // short-circuits a second call, so this effect is safe to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30). Synchronous ref check so a
    // double tap or form submit + Enter within one tick cannot
    // reach `login()` twice before the first POST resolves. The
    // sibling `pending` flag only flips INSIDE
    // `runUploadAndCreate`, i.e. after `login()` resolves, so it
    // does not cover this window on its own.
    if (authSubmittingRef.current) return;
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    // In-flight guard (replacing the startedRef latch, Sebastian
    // ruling 2026-10-28) makes the race between this call-site and
    // the auto-fire effect idempotent. The auto-fire effect fires
    // after login() flips user null → set and gets the SAME in-
    // flight promise from runUploadAndCreate() instead of racing a
    // second run. No explicit claim needed here.
    try {
      await login({ email, password });
      // Defense-in-depth (Sebastian ruling 2026-09-09 — Task 1).
      // authStore.login awaits BOTH POST /api/auth/login AND GET
      // /api/users/me before its promise resolves, then sets
      // { user, status: 'authenticated' } in one store transaction.
      // The overlay reads `user` on next render (from the store
      // subscription) but this handler needs a synchronous guarantee
      // that /me has landed before firing runUploadAndCreate — which
      // calls createOrder against the authenticated session. If a
      // future edit defers /me out of authStore.login, this assertion
      // fails loudly BEFORE runUploadAndCreate fires against a
      // half-populated session. One line, cheap, catches the class
      // of drift that today's login shape would silently break.
      if (useAuthStore.getState().user === null) {
        throw new Error('authStore.login resolved without populating user — /me deferred?');
      }
      await runUploadAndCreate();
    } catch (err) {
      // Backend 429 (login throttled 5/5min per IP) gets the
      // dedicated auth-flow copy so the overlay message matches
      // what the standalone LoginRoute shows for the same window.
      const message =
        err instanceof ApiError && err.httpStatus === 429
          ? STRINGS.auth.errors.loginThrottled
          : STRINGS.book.personen.lightgateLoginError;
      setPhase({ kind: 'login-error', message });
      console.warn('[lightgate] login failed', err);
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30). Same shape as handleLogin.
    // Blocks the second POST on a rapid double tap or submit + Enter
    // combo, which otherwise lands the second request with the
    // already-consumed Turnstile token → `timeout-or-duplicate` →
    // backend 500 (turnstile_challenge_failed) → red flash.
    if (authSubmittingRef.current) return;
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    // In-flight guard (replacing the startedRef latch, Sebastian
    // ruling 2026-10-28) makes the race between this call-site and
    // the auto-fire effect idempotent. The auto-fire effect fires
    // after register() flips user null → set and gets the SAME
    // in-flight promise from runUploadAndCreate() instead of racing
    // a second run. No explicit claim needed here.
    try {
      // `signupSource: 'book_lightgate'` is the implicit marker that
      // routes this customer back into the book flow at
      // `computePostAuthRedirect` Case 2. The backend persists it on
      // `User.signupSource` and returns it via `/me`. Setting it here
      // also means `SignupSourceCard` never renders for a book customer
      // (its `signupSource === null` render-gate).
      // Turnstile token is enforced non-null by `registerFormReady`
      // (submit is disabled until the widget provides one), so
      // reaching this branch guarantees a non-null token. The `??`
      // fallback is a defensive belt only.
      await register({
        email,
        password,
        acceptedTerms: true,
        signupSource: 'book_lightgate',
        turnstileToken: turnstileToken ?? '',
        meta: buildMetaAttributionPayload(),
        marketingObjection: !marketingAccepted,
      });
      // GA4 funnel — sign_up with method='book' (Sebastian ruling
      // 2026-10-21). Fires after a successful register from the
      // book light-gate (customer uploaded photos → signed up to
      // start the book).
      fireGa4SignUp({ method: 'book' });
      await runUploadAndCreate();
    } catch (err) {
      setPhase({
        kind: 'register-error',
        message: STRINGS.book.personen.lightgateRegisterErrorPlaceholder,
      });

      console.warn('[lightgate] register failed', err);
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  // Weiter zum Titelbild stays disabled until:
  //   1. email is filled AND
  //   2. password meets the backend rule (Befund 4, 2026-08-12) —
  //      >= 8 chars, at least one uppercase, at least one digit,
  //      matching `RegisterDto` at `src/auth/dto/register.dto.ts:
  //      26-30`. Pre-fix the client checked only `length > 0`, so
  //      a customer typing "abc" hit the backend 400 that the PWA
  //      swallowed as `lightgateRegisterErrorPlaceholder`.
  //   3. terms are accepted AND
  //   4. Turnstile has provided a non-null token (§A10, 2026-08-07)
  // The turnstileToken guard means a customer who clears his email
  // and reopens the tab 20 min later (token expired via
  // expired-callback → setTurnstileToken(null)) sees a disabled
  // Weiter until the widget refreshes.
  const registerFormReady =
    email.trim().length > 0 &&
    isStrongPassword(password) &&
    termsAccepted &&
    turnstileToken !== null;

  // Sebastian ruling 2026-09-09 — Task 1. Login form is deliberately
  // narrower than register: only email + password gate the submit.
  // No terms checkbox (customer accepted them at account creation),
  // no Turnstile widget (Turnstile is a bot-defense on account
  // creation; login rate-limiting lives on the backend), no password-
  // strength check (backend validates on POST; a returning customer
  // may have a password that doesn't match today's frontend rule).
  const loginFormReady = email.trim().length > 0 && password.length > 0;

  // ═══════════════════════════════════════════════════════════════════
  // Render branch — returning customer (Case 2, §A4).
  //
  // A logged-in customer skips everything that only makes sense at
  // account creation: no title "Fast geschafft.", no subtitle, no
  // email/password form, no terms checkbox, no beruhigungszeile. He
  // sees only the phase heading (photoUploadOverlayHeading during
  // upload, lightgateCreatingOrderHeading during create-order) and
  // any error/retry. The Dialog title carries the phase heading
  // directly so screen readers and sighted users both get the same
  // signal. DialogDescription stays sr-only (Radix requires it for
  // a11y). "Barely an overlay" per Sebastian's ask. Progress counter
  // dropped 2026-09-07 — "N von M hochgeladen" was not information
  // the customer could act on.
  // ═══════════════════════════════════════════════════════════════════
  if (isReturningCustomer) {
    const returningTitle =
      phase.kind === 'creating-order' || phase.kind === 'create-order-error'
        ? STRINGS.book.personen.lightgateCreatingOrderHeading
        : STRINGS.book.personen.photoUploadOverlayHeading;
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={CONTENT_CLASS} data-testid="lightgate-dialog">
          <DialogTitle className="text-lg font-semibold text-dream-navy">
            {returningTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {STRINGS.book.personen.lightgateReturningA11yPlaceholder}
          </DialogDescription>
          {phase.kind === 'upload-error' && (
            <div className="mt-4 space-y-3">
              <p className={ERROR_CLASS}>{phase.message}</p>
              <button
                type="button"
                onClick={() => void runUploadAndCreate()}
                disabled={pending}
                className={BUTTON_GOLD}
              >
                {STRINGS.book.personen.lightgateRetryCta}
              </button>
            </div>
          )}
          {phase.kind === 'photo-unreadable' && (
            <div className="mt-4 space-y-3">
              <p className={ERROR_CLASS}>
                {STRINGS.book.personen.lightgatePhotoUnreadable.replace('{name}', phase.personName)}
              </p>
              <button
                type="button"
                onClick={() => {
                  const key = phase.castKey;
                  onOpenChange(false);
                  onPhotoNeedsReselect?.(key);
                }}
                className={BUTTON_GOLD}
              >
                {STRINGS.book.personen.lightgatePhotoUnreadableCta}
              </button>
            </div>
          )}
          {(phase.kind === 'creating-order' || phase.kind === 'create-order-error') && (
            <div className="mt-4 space-y-3">
              {phase.kind === 'create-order-error' && (
                <>
                  <p className={ERROR_CLASS}>{phase.message}</p>
                  {phase.suppressRetry !== true && (
                    <button
                      type="button"
                      onClick={() => void runUploadAndCreate()}
                      disabled={pending}
                      className={BUTTON_GOLD}
                    >
                      {STRINGS.book.personen.lightgateRetryCta}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // Render branch — anonymous customer.
  //
  // Four-phase shape (Sebastian ruling 2026-09-09 — Task 1 added
  // login as a third form phase alongside register): register form
  // ↔ login form → uploading heading → creating-order heading →
  // success. The two form phases are switchable via link affordances
  // under each primary button. After successful register OR login,
  // the customer sees the upload+create-order phases exactly as the
  // register-only path did before Task 1.
  //
  // DialogTitle is phase-branched: "Fast geschafft." for register
  // (fresh-account signup), "Willkommen zurück" for login (returning
  // customer whose session expired). DialogDescription (subtitle)
  // renders under register — that copy is about the register
  // decision, not the login decision — and is hidden on login (login
  // needs no reassurance about why we ask for account info).
  // ═══════════════════════════════════════════════════════════════════
  const isLoginPhase = phase.kind === 'login' || phase.kind === 'login-error';
  const isRegisterPhase = phase.kind === 'register' || phase.kind === 'register-error';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CONTENT_CLASS} data-testid="lightgate-dialog">
        <DialogTitle className="text-lg font-semibold text-dream-navy">
          {isLoginPhase
            ? STRINGS.book.personen.lightgateLoginHeading
            : STRINGS.book.personen.lightgateTitle}
        </DialogTitle>
        {isLoginPhase ? (
          <DialogDescription className="sr-only">
            {STRINGS.book.personen.lightgateLoginHeading}
          </DialogDescription>
        ) : (
          <DialogDescription className="text-sm text-dream-navy/70">
            {STRINGS.book.personen.lightgateSubtitle}
          </DialogDescription>
        )}

        {isRegisterPhase && (
          <form onSubmit={handleRegister} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="lightgate-email" className="text-sm font-medium text-dream-navy">
                {STRINGS.book.personen.lightgateEmailLabel}
              </label>
              <input
                id="lightgate-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="lightgate-password" className="text-sm font-medium text-dream-navy">
                {STRINGS.book.personen.lightgatePasswordLabel}
              </label>
              <input
                id="lightgate-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={FIELD_CLASS}
              />
              {/*
                Sebastian ruling 2026-09-23 (launch-day feedback):
                the static hint line was invisible-in-effect — a
                customer typing "abc" saw the sentence but not WHICH
                rule was still open, and the "Weiter zur Geschichte"
                button sat silently disabled. Replaced with the live
                `PasswordRequirementsChecklist` (existing component,
                already used on Signup / ResetPassword / Account) with
                `pendingTone="error"` so unmet rules render in
                `text-error` — same red token that drives the
                PersonenEditForm "Es fehlen noch …" line. The button
                stays disabled (`registerFormReady`); the red checklist
                is the visible reason. Rules mirror the backend at
                `src/auth/dto/register.dto.ts:26-30`.
              */}
              <PasswordRequirementsChecklist password={password} tone="light" pendingTone="error" />
            </div>
            <label className="flex items-start gap-2 text-sm text-dream-navy">
              <input
                type="checkbox"
                required
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5"
              />
              <span>{STRINGS.book.personen.lightgateTermsCheckbox}</span>
            </label>
            {/*
              Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked opt-out
              box + hint. Does not gate submit.
            */}
            <div className="space-y-1">
              <label className="flex items-start gap-2 text-sm text-dream-navy">
                <input
                  type="checkbox"
                  checked={marketingAccepted}
                  onChange={(e) => setMarketingAccepted(e.target.checked)}
                  className="mt-0.5"
                />
                <span>{STRINGS.auth.marketingNotice.checkboxLabel}</span>
              </label>
              <p className="pl-[22px] text-xs text-dream-navy/70 leading-relaxed">
                {STRINGS.auth.marketingNotice.hint}
              </p>
            </div>
            {/*
              §A10 Turnstile widget — anon branch only. Renders in
              place; token flows into `turnstileToken` state and gates
              the Weiter button via `registerFormReady`. The widget
              never navigates or reloads the page.
            */}
            <TurnstileWidget onToken={setTurnstileToken} />
            {phase.kind === 'register-error' && <p className={ERROR_CLASS}>{phase.message}</p>}
            <button
              type="submit"
              disabled={!registerFormReady || pending || authSubmitting}
              className={BUTTON_GOLD}
            >
              {STRINGS.book.personen.lightgateRegisterCta}
            </button>
            {/*
              Sebastian ruling 2026-09-09 — Task 1. Link to switch
              the overlay to the login phase. Renders as a text button
              under the primary CTA; clicking swaps the form without
              navigating away (uploads + configurator state preserved).
            */}
            <button
              type="button"
              onClick={() => setPhase({ kind: 'login' })}
              className="w-full text-center text-sm text-dream-navy underline underline-offset-2 hover:text-dream-navy/70"
            >
              {STRINGS.book.personen.lightgateHasAccountLink}
            </button>
            <p className="text-center text-xs text-dream-navy/70">
              {STRINGS.book.personen.beruhigungszeile}
            </p>
          </form>
        )}

        {/*
          Sebastian ruling 2026-09-09 — Task 1. Login form phase.
          Renders when phase.kind is 'login' or 'login-error'. Email
          + password only; no terms (already accepted at register
          time), no Turnstile (bot-defense sits on account creation),
          no password-strength check (backend validates; a returning
          customer may have a password that predates today's rule).
          On success, handleLogin fires runUploadAndCreate exactly
          as the returning-customer auto-fire branch does; the
          customer never sees this form again after success.
        */}
        {isLoginPhase && (
          <form onSubmit={handleLogin} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="lightgate-login-email"
                className="text-sm font-medium text-dream-navy"
              >
                {STRINGS.book.personen.lightgateEmailLabel}
              </label>
              <input
                id="lightgate-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="lightgate-login-password"
                className="text-sm font-medium text-dream-navy"
              >
                {STRINGS.book.personen.lightgateLoginPasswordLabel}
              </label>
              <input
                id="lightgate-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            {phase.kind === 'login-error' && <p className={ERROR_CLASS}>{phase.message}</p>}
            <button
              type="submit"
              disabled={!loginFormReady || pending || authSubmitting}
              className={BUTTON_GOLD}
            >
              {STRINGS.book.personen.lightgateLoginCta}
            </button>
            <button
              type="button"
              onClick={() => setPhase({ kind: 'register' })}
              className="w-full text-center text-sm text-dream-navy underline underline-offset-2 hover:text-dream-navy/70"
            >
              {STRINGS.book.personen.lightgateNoAccountLink}
            </button>
          </form>
        )}

        {(phase.kind === 'uploading' || phase.kind === 'upload-error') && (
          <div className="mt-4 space-y-3">
            <p className="text-base text-dream-navy">
              {STRINGS.book.personen.photoUploadOverlayHeading}
            </p>
            {phase.kind === 'upload-error' && (
              <>
                <p className={ERROR_CLASS}>{phase.message}</p>
                <button
                  type="button"
                  onClick={() => void runUploadAndCreate()}
                  disabled={pending}
                  className={BUTTON_GOLD}
                >
                  {STRINGS.book.personen.lightgateRetryCta}
                </button>
              </>
            )}
          </div>
        )}

        {phase.kind === 'photo-unreadable' && (
          <div className="mt-4 space-y-3">
            <p className={ERROR_CLASS}>
              {STRINGS.book.personen.lightgatePhotoUnreadable.replace('{name}', phase.personName)}
            </p>
            <button
              type="button"
              onClick={() => {
                const key = phase.castKey;
                onOpenChange(false);
                onPhotoNeedsReselect?.(key);
              }}
              className={BUTTON_GOLD}
            >
              {STRINGS.book.personen.lightgatePhotoUnreadableCta}
            </button>
          </div>
        )}

        {(phase.kind === 'creating-order' || phase.kind === 'create-order-error') && (
          <div className="mt-4 space-y-3">
            <p className="text-base text-dream-navy">
              {STRINGS.book.personen.lightgateCreatingOrderHeading}
            </p>
            {phase.kind === 'create-order-error' && (
              <>
                <p className={ERROR_CLASS}>{phase.message}</p>
                {phase.suppressRetry !== true && (
                  <button
                    type="button"
                    onClick={() => void runUploadAndCreate()}
                    disabled={pending}
                    className={BUTTON_GOLD}
                  >
                    {STRINGS.book.personen.lightgateRetryCta}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
